import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MarketingUsersStoreService } from '../../../services/marketing-users/marketing-users.store.service';

/** The segments' order, hottest first: the strip and the sort follow it. */
const SEGMENT_ORDER = ['hot', 'warm', 'cool', 'frozen', 'groupOnly', 'untouched'];

const PAGE_SIZE = 25;

/**
 * The marketing view of the user base. Every row answers three questions in
 * order: who is this, what did they last do, how can I reach them. Heat uses
 * the segments screen's rule and colors, so both screens tell one story.
 */
@Component({
  selector: 'bo-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersComponent implements OnInit {
  private readonly store = inject(MarketingUsersStoreService);

  readonly rows = this.store.rows;
  readonly total = this.store.total;
  readonly loading = this.store.loading;
  readonly failed = this.store.failed;

  readonly page = signal(1);
  readonly search = signal('');
  readonly segment = signal<string | null>(null);
  readonly reachableOnly = signal(false);

  readonly pageSize = PAGE_SIZE;
  readonly lastPage = computed(() => Math.max(Math.ceil(this.total() / PAGE_SIZE), 1));

  readonly heatStrip = computed(() =>
    SEGMENT_ORDER.map((key) => ({ key, count: this.store.segmentCounts()[key] ?? 0 })),
  );

  ngOnInit(): void {
    this.reload();
  }

  private reload(): void {
    this.store.load({
      page: this.page(),
      limit: PAGE_SIZE,
      search: this.search(),
      segment: this.segment() ?? undefined,
      reachableMarketing: this.reachableOnly() || undefined,
    });
  }

  submitSearch(value: string): void {
    this.search.set(value);
    this.page.set(1);
    this.reload();
  }

  toggleSegment(key: string): void {
    this.segment.set(this.segment() === key ? null : key);
    this.page.set(1);
    this.reload();
  }

  toggleReachable(): void {
    this.reachableOnly.set(!this.reachableOnly());
    this.page.set(1);
    this.reload();
  }

  goTo(page: number): void {
    if (page < 1 || page > this.lastPage()) {
      return;
    }
    this.page.set(page);
    this.reload();
  }

  initials(firstName: string, lastName: string): string {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || '?';
  }
}
